import { useMemo, useState } from 'react';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';

export type SearchableSelectOption = {
  value: string;
  label: string;
};

type SearchableSelectProps = {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  showClear?: boolean;
};

/**
 * Type-to-filter dropdown for a fixed local list.
 *
 * WI 37941 #10: these lists were plain `Select`s, whose typeahead only jumps to the first
 * letter and then stops -- typing "MIR" lands at the top of the M's and you scroll from there.
 * Miranda flagged it on the Validator list and noted the behaviour "shows up in a lot of
 * places". Substring matching across the whole label is what the original Freeze app does.
 *
 * Filtering is done here rather than left to the combobox primitive: every other combobox in
 * the app (accounts, sites) filters server-side via `onInputValueChange`, so there is no local
 * precedent to inherit and relying on an implicit default would be easy to break silently.
 */
export const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  emptyMessage = 'No matches found',
  disabled,
  className,
  showClear = true,
}: SearchableSelectProps) => {
  const [query, setQuery] = useState('');

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <Combobox<SearchableSelectOption>
      items={filtered}
      value={selected}
      onValueChange={(option) => {
        onChange(option?.value ?? '');
        setQuery('');
      }}
      onInputValueChange={setQuery}
      itemToStringValue={(o) => o.value}
      itemToStringLabel={(o) => o.label}
      disabled={disabled}
    >
      <ComboboxInput
        className={className}
        placeholder={placeholder}
        showClear={showClear}
        disabled={disabled}
      />
      <ComboboxContent>
        <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
        <ComboboxList>
          {(option: SearchableSelectOption) => (
            <ComboboxItem key={option.value} value={option}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default SearchableSelect;
